import axios from 'axios';
import {store} from '../store';
import {URLS} from '../config';
import {authService} from './authService';
import {clearAuth, setTokens} from '../store/slices/authSlice';
import {listCache} from '../utils/listCache';

const axiosInstance = axios.create({
  baseURL: URLS.API_BASE_URL,
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add the access token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const accessToken = state.authSlice.accessToken;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Cegah Huawei browser meng-cache API response GET
    if (config.method === 'get' || config.method === 'GET') {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh logic
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 and it's not a retry request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; // Mark the request to avoid infinite loops

      try {
        const state = store.getState();
        const {refreshToken, userId, userName, email, groupId, permissions} = state.authSlice;

        if (!refreshToken) {
          // If no refresh token is available, logout immediately.
          listCache.clearAll();
          store.dispatch(clearAuth());
          window.location.href = '/SignIn';
          return Promise.reject(error);
        }

        // Attempt to refresh the token
        const newTokens = await authService.refreshToken(refreshToken);
        
        // Fix: Jika backend tidak mengembalikan refreshToken baru, gunakan yang lama agar tidak hilang
        // Fix: Pertahankan userId dan userName jika tidak dikembalikan oleh endpoint refresh
        const finalTokens = {
          accessToken: newTokens.accessToken!,
          refreshToken: newTokens.refreshToken || refreshToken,
          userId: newTokens.user?.id || userId,
          userName: newTokens.user?.name || userName,
          email: newTokens.user?.email || email,
          groupId: (newTokens.user as any)?.group_id || groupId,
          permissions: (newTokens.user as any)?.permissions || permissions,
        };
        
        store.dispatch(setTokens(finalTokens));

        // Update the authorization header and retry the original request
        axiosInstance.defaults.headers.common['Authorization'] =
          `Bearer ${finalTokens.accessToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${finalTokens.accessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, clear all auth data and redirect to login
        listCache.clearAll();
        store.dispatch(clearAuth());
        window.location.href = '/SignIn';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;